// Type-safe API client. All methods return Promise<T> and throw ApiError on
// non-2xx responses. 401 auto-redirects to /login.

import type {
  AdminSettings,
  AdminSettingsPatch,
  Character,
  ContiResponse,
  CreateCharacterInput,
  CreatePublishJobInput,
  CreateScriptInput,
  CreateStoryInput,
  ExportFormat,
  FactoryConfig,
  FactoryConfigInput,
  FactoryRun,
  FactoryTopic,
  PublishAccount,
  PublishJob,
  PublishQueueItem,
  SpikeView,
  SynopsisResult,
  KreaLoraCatalog,
  ProjectListItem,
  ReferenceAdaptInput,
  ReferenceResult,
  RenderStatus,
  RenderStoryInput,
  ScriptResponse,
  SectionName,
  ShootingPlan,
  SceneClip,
  Story,
  Style,
  TrendCategory,
  TrendTopic,
  TrendTopicsQuery,
  UpdateCharacterInput,
  UpdateStoryInput,
  Video,
} from './types'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(
      res.status,
      (body as { error?: string } | null)?.error ?? res.statusText,
      body,
    )
  }
  // Tolerate empty bodies on any 2xx (e.g. 202 Accepted from approve/render,
  // 204 No Content) — calling res.json() on an empty body throws
  // "Unexpected end of JSON input". Parse only when there's a body.
  const text = await res.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

// ─── Types ────────────────────────────────────────────────────────────────

export interface User {
  id: number
  email: string
  name: string
}

export interface Stats {
  users: number
  active_sessions: number
}

export interface VersionInfo {
  commit: string
  built_at: string
}

// Beat is one ordered section of a preset's structure template (migration 0028).
export interface Beat {
  label: string
  desc: string
}

// PresetFormat routes default length/shape: short (~60s) | long (~20min) | both.
export type PresetFormat = 'short' | 'long' | 'both'

// Channel profile (R5 / plan U4 + migration 0028). Voice is 3–7 adjectives;
// sample_scripts is 0–3 entries (validation enforced backend-side; UI mirrors
// limits). The preset fields steer tone/structure/tags/thumbnail/directing.
export interface ChannelProfile {
  id: number
  slug: string
  name: string
  audience: string
  voice: string[]
  taboo_words: string[]
  sample_scripts: string[]
  default_cta: string
  bgm_preference: string
  thumbnail_style: string
  format: PresetFormat
  structure: Beat[]
  title_rule: string
  mandatory_tags: string[]
  tag_style: string
  directing_style: string
  reference_guide: string
  created_at: number
  updated_at: number
}

export interface ChannelProfileInput {
  slug?: string
  name: string
  audience: string
  voice: string[]
  taboo_words: string[]
  sample_scripts: string[]
  default_cta: string
  bgm_preference: string
  thumbnail_style: string
  format: PresetFormat
  structure: Beat[]
  title_rule: string
  mandatory_tags: string[]
  tag_style: string
  directing_style: string
  reference_guide: string
}

// ─── API ──────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),

  // Meta
  health: () => request<{ ok: boolean }>('/api/health'),
  version: () => request<VersionInfo>('/api/version'),
  stats: () => request<Stats>('/api/stats'),

  // Channel profiles
  listProfiles: () => request<ChannelProfile[]>('/api/channel-profiles'),
  getProfile: (id: number) => request<ChannelProfile>(`/api/channel-profiles/${id}`),
  createProfile: (input: ChannelProfileInput) =>
    request<ChannelProfile>('/api/channel-profiles', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateProfile: (id: number, input: ChannelProfileInput) =>
    request<ChannelProfile>(`/api/channel-profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteProfile: (id: number) => request<void>(`/api/channel-profiles/${id}`, { method: 'DELETE' }),

  // Topics (trend spikes — the create surface's topic source)
  recentSpikes: () => request<SpikeView[]>('/api/topics/recent_spikes'),
  refreshTopics: () =>
    request<{ fetched: number; advanced: boolean }>('/api/topics/refresh', {
      method: 'POST',
    }),

  // Sources hub — proxy of trend-collector's live source catalog. Categories
  // feed the tag picker; topics are the browsable clustered issues.
  getTrendCategories: () =>
    request<{ categories: TrendCategory[] }>('/api/trend-categories').then((r) => r.categories),
  getTrendTopics: (params: TrendTopicsQuery = {}): Promise<TrendTopic[]> => {
    const q = new URLSearchParams()
    if (params.tag) q.set('tag', params.tag)
    if (params.min_score != null && params.min_score > 0)
      q.set('min_score', String(params.min_score))
    if (params.limit != null) q.set('limit', String(params.limit))
    if (params.spike_only) q.set('spike_only', '1')
    const qs = q.toString()
    return request<{ topics: TrendTopic[] }>(`/api/trend-topics${qs ? `?${qs}` : ''}`).then(
      (r) => r.topics,
    )
  },

  // Reference video adaptation (Sources tab ④). `url` may be a single video OR a
  // playlist/channel link — the backend expands playlists and processes each
  // video sequentially (transcript → story), so a multi-video URL can block for
  // a while. Returns one result per expanded video: story_id = success (link to
  // /stories), error = that video failed. A 503 means the feature isn't
  // configured server-side (yt-dlp / LLM missing).
  postReferenceVideo: (body: ReferenceAdaptInput): Promise<ReferenceResult[]> =>
    request<{ results: ReferenceResult[] }>('/api/reference-video', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.results),

  // Script create + interactive synopsis preview (Phase 5)
  synopses: (input: CreateScriptInput) =>
    request<SynopsisResult>('/api/scripts/synopses', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  createScript: (input: CreateScriptInput) =>
    request<ScriptResponse>('/api/scripts', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // Publish (YouTube upload + approval queue)
  listPublishAccounts: () => request<PublishAccount[]>('/api/publish/accounts'),
  deletePublishAccount: (id: number) =>
    request<void>(`/api/publish/accounts/${id}`, { method: 'DELETE' }),
  publishQueue: () => request<PublishQueueItem[]>('/api/publish/queue'),
  createPublishJob: (input: CreatePublishJobInput) =>
    request<PublishJob>('/api/publish/jobs', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  retryPublishJob: (id: number) =>
    request<PublishJob>(`/api/publish/jobs/${id}/retry`, { method: 'POST' }),
  listPublishJobs: (videoId: number) =>
    request<PublishJob[]>(`/api/publish/jobs?video_id=${videoId}`),

  // Factory (scheduled video production)
  listFactoryConfigs: () => request<FactoryConfig[]>('/api/factory/configs'),
  createFactoryConfig: (input: FactoryConfigInput) =>
    request<FactoryConfig>('/api/factory/configs', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateFactoryConfig: (id: number, input: FactoryConfigInput) =>
    request<FactoryConfig>(`/api/factory/configs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteFactoryConfig: (id: number) =>
    request<void>(`/api/factory/configs/${id}`, { method: 'DELETE' }),
  runFactoryNow: (id: number, topic?: string) =>
    request<FactoryRun>(`/api/factory/configs/${id}/run`, {
      method: 'POST',
      body: JSON.stringify({ topic: topic ?? '' }),
    }),
  listFactoryTopics: () => request<FactoryTopic[]>('/api/factory/topics'),
  listFactoryRuns: (configId?: number) =>
    request<FactoryRun[]>(`/api/factory/runs${configId ? `?config_id=${configId}` : ''}`),
  approveFactoryRun: (id: number) =>
    request<void>(`/api/factory/runs/${id}/approve`, { method: 'POST' }),
  rejectFactoryRun: (id: number) =>
    request<void>(`/api/factory/runs/${id}/reject`, { method: 'POST' }),
  deleteFactoryRun: (id: number) => request<void>(`/api/factory/runs/${id}`, { method: 'DELETE' }),

  // Scripts (read-only listing + editor)
  getScript: (id: number) => request<ScriptResponse>(`/api/scripts/${id}`),
  listScripts: () => request<ProjectListItem[]>('/api/scripts'),

  // Regenerate a single section. Body is reserved for future prompt overrides;
  // sending {} keeps the wire format forward-compatible.
  regenerateSection: (id: number, section: SectionName) =>
    request<ScriptResponse>(`/api/scripts/${id}/regenerate/${section}`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  // Storyboard (video pipeline R1). getConti reads the stored ContiPackage
  // (payload omitted until first decompose); decomposeScript runs the LLM
  // decompose phase and returns the fresh storyboard.
  getConti: (id: number) => request<ContiResponse>(`/api/scripts/${id}/conti`),
  decomposeScript: (id: number) =>
    request<ContiResponse>(`/api/scripts/${id}/decompose`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  // Stories (storygen R2). Create is synchronous — the request blocks while
  // the planner→director→critic chain runs, then returns the persisted story
  // row (status=ready + shooting_plan on success).
  listStories: () => request<Story[]>('/api/stories'),
  getStory: (id: number) => request<Story>(`/api/stories/${id}`),
  createStory: (input: CreateStoryInput) =>
    request<Story>('/api/stories', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateStory: (id: number, input: UpdateStoryInput) =>
    request<Story>(`/api/stories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  updateStoryPlan: (id: number, plan: ShootingPlan) =>
    request<Story>(`/api/stories/${id}/plan`, {
      method: 'PATCH',
      body: JSON.stringify(plan),
    }),
  retryStory: (id: number) => request<Story>(`/api/stories/${id}/retry`, { method: 'POST' }),
  deleteStory: (id: number) => request<void>(`/api/stories/${id}`, { method: 'DELETE' }),

  // Characters (화풍 catalog + async generation). createCharacter returns a
  // `generating` row (202); the UI polls listCharacters until status flips to
  // ready|error.
  listStyles: () => request<Style[]>('/api/styles'),
  listKreaLoras: () => request<KreaLoraCatalog>('/api/krea-loras'),
  listCharacters: () => request<Character[]>('/api/characters'),
  getCharacter: (id: number) => request<Character>(`/api/characters/${id}`),
  createCharacter: (input: CreateCharacterInput) =>
    request<Character>('/api/characters', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateCharacter: (id: number, input: UpdateCharacterInput) =>
    request<Character>(`/api/characters/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  retryCharacter: (id: number) =>
    request<Character>(`/api/characters/${id}/retry`, { method: 'POST' }),
  deleteCharacter: (id: number) => request<void>(`/api/characters/${id}`, { method: 'DELETE' }),

  // Videos (videorender). renderStory kicks an async GPU render of the story's
  // shooting_plan and returns a `generating` row (202); the UI polls listVideos
  // until status flips to ready|error, then plays /media/videos/<id>.mp4.
  listVideos: () => request<Video[]>('/api/videos'),
  getVideo: (id: number) => request<Video>(`/api/videos/${id}`),
  renderStory: (storyId: number, input: RenderStoryInput) =>
    request<Video>(`/api/stories/${storyId}/render`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  retryVideo: (id: number) => request<Video>(`/api/videos/${id}/retry`, { method: 'POST' }),
  deleteVideo: (id: number) => request<void>(`/api/videos/${id}`, { method: 'DELETE' }),
  // Per-scene clips: list, re-render one, merge ready clips → final mp4.
  listSceneClips: (videoId: number) => request<SceneClip[]>(`/api/videos/${videoId}/scenes`),
  renderScene: (videoId: number, idx: number) =>
    request<SceneClip>(`/api/videos/${videoId}/scenes/${idx}/render`, { method: 'POST' }),
  cancelRender: (videoId: number) =>
    request<void>(`/api/videos/${videoId}/cancel`, { method: 'POST' }),
  mergeVideo: (videoId: number) =>
    request<Video>(`/api/videos/${videoId}/merge`, { method: 'POST' }),

  startRender: (id: number) =>
    request<RenderStatus>(`/api/scripts/${id}/render`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  renderStatus: (id: number) => request<RenderStatus>(`/api/scripts/${id}/render/status`),

  // Admin settings (U9). PUT accepts a partial — omit fields you don't want
  // to touch, send SECRET_UNCHANGED for masked secrets you don't want to
  // overwrite. Returns the post-write state in one round-trip.
  getAdminSettings: () => request<AdminSettings>('/api/admin/settings'),
  putAdminSettings: (patch: AdminSettingsPatch) =>
    request<AdminSettings>('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  // Export — fetch the rendered document body as text. Used by the "Copy
  // full script" button. The download buttons skip this and let the browser
  // hit the URL directly so Content-Disposition can drive the file save.
  exportScriptText: async (id: number, format: ExportFormat): Promise<string> => {
    const res = await fetch(scriptExportUrl(id, format), { credentials: 'include' })
    if (res.status === 401) {
      if (!location.pathname.startsWith('/login')) window.location.href = '/login'
      throw new ApiError(401, 'unauthorized')
    }
    if (!res.ok) {
      throw new ApiError(res.status, res.statusText)
    }
    return res.text()
  },
}

// scriptExportUrl is exported separately so the ExportButton component can
// build a download href without going through the api object — that keeps
// the button stateless and lets the browser handle the file save.
export function scriptExportUrl(id: number, format: ExportFormat): string {
  return `/api/scripts/${id}/export?format=${encodeURIComponent(format)}`
}

export function renderDownloadUrl(id: number): string {
  return `/api/scripts/${id}/render/download`
}
